import { POSITION_COUNTS } from '../constants/teambuilder';

/**
 * Extract team abbreviation from comma-separated team string
 */
export const getTeamAbbrev = (teamAbbrevs) => {
  return teamAbbrevs.split(/[,]+/).pop();
};

/**
 * Get player's display name based on position
 */
export const getPlayerName = (player) => {
  return player.positionCode === 'G'
    ? player.goalieFullName
    : player.skaterFullName;
};

/**
 * Get stats display string based on position
 */
export const getPlayerStatsDisplay = (player) => {
  const { positionCode, gamesPlayed } = player;

  if (positionCode === 'G') {
    const { wins, losses, otLosses } = player;
    return `GP: ${gamesPlayed} W: ${wins} L: ${losses} OTL: ${otLosses}`;
  }

  const { goals, assists, points } = player;
  return `GP: ${gamesPlayed} G: ${goals} A: ${assists} P: ${points}`;
};

/**
 * Find utility player: last player added who exceeded their position limit
 */
export const findUtilityPlayer = (myTeam) => {
  const utilityPlayerIndex = [...myTeam]
    .reverse()
    .findIndex((p) => {
      const positionCount = myTeam.filter(
        (player) => player.positionCode === p.positionCode
      ).length;
      return positionCount > POSITION_COUNTS[p.positionCode];
    });

  return utilityPlayerIndex !== -1 ? myTeam[myTeam.length - 1 - utilityPlayerIndex] : null;
};

/**
 * Calculate how many of each position are selected
 */
export const calculatePositionLimits = (myTeam) => {
  return myTeam.reduce((acc, player) => {
    acc[player.positionCode] = (acc[player.positionCode] || 0) + 1;
    return acc;
  }, {});
};

/**
 * Calculate how many players from each team are selected
 */
export const calculateTeamCount = (myTeam) => {
  return myTeam.reduce((acc, player) => {
    const team = getTeamAbbrev(player.teamAbbrevs);
    acc[team] = (acc[team] || 0) + 1;
    return acc;
  }, {});
};

/**
 * Check if utility bonus is available
 * (positions that can accept one extra player)
 * Returns 0 if a utility player has already been assigned, 1 if slot is available
 */
export const calculateUtilityBonus = (positionLimit) => {
  // If any position has exceeded its count, utility slot is taken
  const hasUtilityPlayer = Object.entries(POSITION_COUNTS).some(
    ([position, limit]) => positionLimit[position] > limit
  );
  return hasUtilityPlayer ? 0 : 1;
};

/**
 * Check if a player can be selected
 * Returns true if position is full and no utility bonus
 */
export const isPlayerDisabled = (player, positionLimit, utilityBonus) => {
  const limit = POSITION_COUNTS[player.positionCode];
  return positionLimit[player.positionCode] >= limit + utilityBonus;
};

/**
 * Build the roster IDs object for submission
 */
export const buildTeamIds = (myTeam, userId) => {
  const utilityPlayer = findUtilityPlayer(myTeam);

  const remainingPlayers = [...myTeam].filter((p) => p.playerId !== utilityPlayer?.playerId);

  // Helper to get player IDs for a specific position
  const getPlayersByPosition = (players, positionCode) =>
    players.filter((p) => p.positionCode === positionCode).map((p) => p.playerId);

  return {
    owner: userId,
    center: getPlayersByPosition(remainingPlayers, 'C'),
    left: getPlayersByPosition(remainingPlayers, 'L'),
    right: getPlayersByPosition(remainingPlayers, 'R'),
    defense: getPlayersByPosition(remainingPlayers, 'D'),
    goalie: getPlayersByPosition(remainingPlayers, 'G'),
    utility: utilityPlayer ? [utilityPlayer.playerId] : [],
  };
};

/**
 * Filter players by name, team, and position
 */
export const filterPlayers = (players, nameSearch, teamFilter, positionFilter) => {
  return players.filter((player) => {
    const matchesPosition = positionFilter.length
      ? positionFilter.includes(player.positionCode)
      : true;

    const matchesTeam = teamFilter.length
      ? teamFilter.includes(getTeamAbbrev(player.teamAbbrevs))
      : true;

    const playerName = getPlayerName(player).toLowerCase();
    const matchesName = nameSearch
      ? playerName.includes(nameSearch.toLowerCase())
      : true;

    return matchesPosition && matchesTeam && matchesName;
  });
};

/**
 * Sort players by relevant stat
 */
export const sortPlayers = (players, isGoalie) => {
  return [...players].sort((a, b) => {
    if (isGoalie) {
      return b.wins - a.wins;
    }
    return b.points - a.points;
  });
};

/**
 * Get individual stat value from player (GP, G/W, A/L, P/OTL)
 */
export const getStatValue = (player, statIndex) => {
  const { positionCode, gamesPlayed } = player;

  if (statIndex === 0) {
    return gamesPlayed; // GP for both
  }

  if (positionCode === 'G') {
    const { wins, losses, otLosses } = player;
    if (statIndex === 1) return wins;    // W
    if (statIndex === 2) return losses;  // L
    if (statIndex === 3) return otLosses; // OTL
  }

  // Skaters
  const { goals, assists, points } = player;
  if (statIndex === 1) return goals;   // G
  if (statIndex === 2) return assists; // A
  if (statIndex === 3) return points;  // P
};

/**
 * Get stat label for column based on position and stat index
 */
export const getStatLabel = (player, statIndex) => {
  if (statIndex === 0) return 'GP';
  if (statIndex === 1) return player.positionCode === 'G' ? 'W' : 'G';
  if (statIndex === 2) return player.positionCode === 'G' ? 'L' : 'A';
  if (statIndex === 3) return player.positionCode === 'G' ? 'OTL' : 'P';
};

/**
 * Create a reusable table header row with standard columns (split stats into 4 columns)
 */
export const createTableHeader = () => {
  const { Table } = require('semantic-ui-react');
  const { TABLE_HEADERS } = require('../constants/teambuilder');
  return (
    <Table.Header>
      <Table.Row>
        <Table.HeaderCell>{TABLE_HEADERS.ACTION}</Table.HeaderCell>
        <Table.HeaderCell>{TABLE_HEADERS.POSITION}</Table.HeaderCell>
        <Table.HeaderCell>{TABLE_HEADERS.NAME}</Table.HeaderCell>
        <Table.HeaderCell>{TABLE_HEADERS.TEAM}</Table.HeaderCell>
        <Table.HeaderCell colSpan='4'>Season Stats</Table.HeaderCell>
      </Table.Row>
    </Table.Header>
  );
};
